import React, { useEffect, useMemo, useState } from 'react';
import MaterialTable, {MTableToolbar} from "material-table";
import TextField from '@mui/material/TextField';
import Select from 'react-select';

import { styledTable, styledTableHeading, tableIcons } from '../../../helpers';
import useAPI from '../../../hooks/useAPI';
import {
  localization, modifyDefaultOptions, options, tableIconsModified,
} from '../../../helpers/table';
import { useAPICostKvrs } from '../adminApiService';
import { useAPICostAgreements } from '../adminApiService';
import { years } from '../../../helpers';

export default function TabCostKvrs() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [agrs, setAgrs] = useState([]);
  const [agrsYears, setAgrsYears] = useState({});
  const [data, setData] = useState([]);

  const api = useAPI();
  const [getData, saveData, removeData] = useMemo(
    () => useAPICostKvrs(api),
    [api],
  );

  const [getDataAgr] = useMemo(
    () => useAPICostAgreements(api),
    [api],
  );

  useEffect(async () => {
    let result = await getData();

    const respAgrs = await api.get("/mng/cost/agrs");
    if (respAgrs && !respAgrs.error) {
      setAgrs(respAgrs);
    }

    const CostAgreements = await getDataAgr();
    let agreements = CostAgreements.map((e) => {
      const years = e.agreements.map((e) => e.num.split("-")[2]);
      e.years = [...new Set(years)];
      return e;
    });
    agreements = agreements.reduce((prev, e) => ({...prev, [e.num] : e.years}),{});
    setAgrsYears(agreements)
    setIsLoaded(true);
    setData(result);
  }, []);

  const handleSaveData = async (entry, isNew) => {
    const resp = await saveData(entry);
    if (resp && !resp.error) {
      isNew
        ? await setData((prev) => [resp, ...prev])
        : await setData((prev) => prev.map((kvr) => (kvr.id === resp.id ? resp : kvr)));
      return true;
    }
    return false;
  };

  const handleRemoveData = async (entry) => {
    const resp = await removeData(entry);
    if (resp && !resp.error && resp.success) {
      await setData((prev) => prev.filter((kvr) => kvr.id !== entry.id));
      return true;
    }
    return false;
  };

  const mapAgrsToOptions = (agrs) => (agrs ? agrs.map((k) => ({ label: `${k.num} ${k.name}`, value: k.id })) : []);
  const onAgrMultiselectChange = (selectedOptions, onChangeHandler) => {
    const newVals = agrs.filter(k => selectedOptions.map(sO => sO.value).includes(k.id));
    onChangeHandler(newVals)
  }

  const arrayToLookupNum = (values) => {
    const key = "num";
    if (!values) return {};
    const filteredValues = values.filter((e) => e[key])
    if (filteredValues.length === 0) return false
    return Object.fromEntries(
        filteredValues
        .sort((a, b) => {
          return a[key] === b[key] ? 0 : a[key] < b[key] ? -1 : 1;
        })
        .map((v) => [v[key], v[key || key]])
    );
  };

  const arrayToLookupArgs = (values) => {
    if (!values) return {};
    if (values.length === 0) return false;
    return Object.fromEntries(values.map((v) => [v.id, v.num + " " + v["name"]]));
  };

  const columns = [
    {
      title: "Код статьи КВР",
      field: "num",
      lookup: arrayToLookupNum(data),
      maxWidth: 150,
      editComponent: ({ rowData, value, onChange }) => {
        return <TextField variant="standard" value={value} onChange={(e) => onChange(e.target.value)} />;
      },
    },
    {
      title: "Наименование статьи затрат (КВР)",
      field: "name",
      editComponent: ({ rowData, value, onChange }) => {
        return <TextField fullWidth variant="standard" value={value} onChange={(e) => onChange(e.target.value)} />;
      },
    },
    {
      title: "Статьи затрат (Соглашение)",
      field: "costAgrs",
      render: (rowData) => (
        <div style={{ whiteSpace: "pre-wrap" }}>
          {rowData.costAgrs.map((kvr) => `${kvr.num}\t${kvr.name}`).join("\n")}
        </div>
      ),
      customFilterAndSearch: (term, rowData) => {
        if (term.length == 0) return true;
        term = term.map((e) => Number(e));
        const rowDataArgs = rowData.costAgrs.map((e) => e.id);
        const res = term.filter((x) => rowDataArgs.includes(x));
        return res.length > 0;
      },
      editComponent: ({ rowData, value, onChange }) => (
        <Select
          placeholder=""
          isMulti
          menuPlacement="auto"
          options={mapAgrsToOptions(agrs)}
          onChange={(selValues) => onAgrMultiselectChange(selValues, onChange)}
          value={mapAgrsToOptions(value)}
          closeMenuOnSelect={false}
          components={{ IndicatorSeparator: () => null, ClearIndicator: () => null }}
          styles={selectStyles}
          menuPortalTarget={document.body}
        />
      ),
      lookup: arrayToLookupArgs(agrs),
    },
    {
      title: "Года соглашений",
      render: (rowData) => (
        <div style={{ whiteSpace: "pre-wrap" }}>
          {rowData.costAgrs.map((kvr) => agrsYears[kvr.num].join(", ")).join("\n")}
        </div>
      ),
      maxWidth: 150,
      editComponent: () => null,
      customFilterAndSearch: (term, rowData) => {
        if (term.length == 0) return true;
        let rowYears=rowData.costAgrs.map((kvr) => agrsYears[kvr.num].join(" ")).join(" ").split(" ")
        const res = term.filter((x) => rowYears.includes(x));
        return res.length > 0;
      },
      lookup:years.reduce((prev, e)=>({...prev,[e]:e}),{})
    },
  ];

  if (!isLoaded) {
    return (
      <section className="tab-agreement-plan">
        <div>Загрузка...</div>
      </section>
    );
  } else
    return (
      <section className="page tab-statement-events">
        <div style={styledTable}>
          <MaterialTable
            components={{
              Container: (props) => <div className="special-filter-noWidth" {...props} />,
              Toolbar: (props) => (
                <div className="d-flex align-items-center justify-content-between">
                  <h3 style={styledTableHeading}>Статьи затрат (КВР)</h3>
                  <div className="d-flex align-items-center" style={{ zIndex: "999999" }}>
                    {/* <Select
                      placeholder="Выберите год"
                      defaultValue={year}
                      options={optionsYears}
                      onChange={(selected) => {
                        setYear(selected);
                      }}
                    /> */}
                    <MTableToolbar {...props} />
                  </div>
                </div>
              ),
            }}
            title=""
            options={modifyDefaultOptions({
              actionsColumnIndex: -1,
              addRowPosition: "first",
              search: false,
              toolbar: true,
            })}
            icons={tableIconsModified}
            localization={localization}
            columns={columns}
            data={data}
            editable={{
              onRowAdd: newData =>
                new Promise(async (resolve, reject) => {
                  const success = await handleSaveData(newData, true);
                  if(success)
                    resolve();
                  else
                    reject();
                }),
              onRowUpdate: (newData, oldData) =>
              new Promise(async (resolve, reject) => {
                const success = await handleSaveData(newData);
                if (success) resolve();
                else reject();
              }),
            onRowDelete: (oldData) =>
              new Promise(async (resolve, reject) => {
                const success = await handleRemoveData(oldData);
                resolve();
              }),
          }}
        />
      </div>
    </section>
  );
}

const selectStyles = {
  valueContainer: (provided) => ({...provided, flexDirection: 'column', alignItems: 'flex-start'}),
  multiValue: (provided) => ({...provided, flexDirection: 'row-reverse' }),
  multiValueLabel: (provided) => ({...provided, whiteSpace: undefined }),
  menuPortal: (provided) => ({ ...provided, zIndex: 99999 })
}
